/**
 * Which Masters party a name read off a document is.
 *
 * Exact first: the party name, legal name, nickname or an alias, ignoring case, spacing
 * and punctuation ("India Palace Restaurant." = "india palace restaurant").
 *
 * Then the same names without a trailing ", <place>": a party kept as
 * "India Palace Restaurant, Regina" is the receipt's "India Palace Restaurant" (2026-10-19:
 * live showed it as "not in Masters"). Used only when it points at ONE party — with
 * "Tim Hortons, Regina" and "Tim Hortons, Saskatoon" both kept, "Tim Hortons" stays unmatched.
 */
export interface NamedParty {
  name: string;
  legal_name: string;
  nickname?: string | null;
  aliases: string[];
}

export const normPartyName = (s: string): string =>
  String(s || '').toLowerCase().replace(/&/g, ' and ').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/** "india palace restaurant, regina" → "india palace restaurant"; '' when there is no ", place". */
const withoutPlace = (s: string): string => {
  const i = String(s || '').lastIndexOf(',');
  return i > 0 ? normPartyName(s.slice(0, i)) : '';
};

const namesOf = (m: NamedParty): string[] =>
  [m.name, m.legal_name, m.nickname || '', ...m.aliases].filter(n => n && n.trim());

/** The parties `q` names: exact matches, else the one-party match without ", place". */
export function partiesNamed<T extends NamedParty>(parties: T[], q: string): T[] {
  const key = normPartyName(q);
  if (!key) return [];
  const exact = parties.filter(m => namesOf(m).some(n => normPartyName(n) === key));
  if (exact.length) return exact;
  const loose = parties.filter(m => namesOf(m).some(n => withoutPlace(n) === key));
  // the same party may be listed more than once (own books + shared copy share a name)
  const distinct = new Set(loose.map(m => normPartyName(m.name)));
  return distinct.size === 1 ? loose : [];
}
